import {
  Box,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalCloseButton,
  ModalBody,
  Text,
  Button,
  ModalFooter,
} from '@chakra-ui/react'
import Compressor from 'compressorjs'
// import Cropper from 'react-cropper';
import { FixedCropper, ImageRestriction, StencilSize, DefaultSize } from 'react-advanced-cropper'
import 'react-advanced-cropper/dist/style.css'

import { useRef, useState, useEffect } from 'react'

const dataUrlToFile = (url: string, fileName: string) => {
  // Split the URL into the media type and data parts
  const [mediaType, data] = url.split(',')

  // Extract the MIME type using a regular expression
  const mimeMatch = mediaType.match(/:(.*?);/)
  const mime = mimeMatch ? mimeMatch[1] : ''

  // Decode the Base64-encoded data part
  const byteString = atob(data)

  // Create a Uint8Array to hold the binary data
  const n = byteString.length
  const arr = new Uint8Array(n)

  // Convert the binary data to the Uint8Array
  for (let i = 0; i < n; i++) {
    arr[i] = byteString.charCodeAt(i)
  }

  // Create and return a File object
  return new File([arr], fileName, { type: mime })
}

const ImageCropModal = ({
  image,
  isOpen,
  onClose,
  onSave,
  isLoading,
}: {
  image: string
  isOpen: boolean
  onClose: () => void
  onSave: (v: File | null) => void
  isLoading: boolean
}) => {
  const cropperRef = useRef<any>(null)
  const containerRef = useRef<HTMLDivElement>(null)
  const [isCropperReady, setIsCropperReady] = useState(false)
  const [cropperWidth, setCropperWidth] = useState(0)
  const [isCompressing, setIsCompressing] = useState(false)

  const onCrop = async () => {
    const cropper = cropperRef.current
    if (!cropper) return

    // Export at 1400x1750 (4:5 aspect ratio)
    const canvas = cropper.getCanvas({ width: 1400, height: 1750 })
    const imageName = 'image.png'
    const finalImage = dataUrlToFile(canvas.toDataURL(), imageName)

    setIsCompressing(true)
    try {
      const compressedBlob = await new Promise<Blob>((resolve, reject) => {
        new Compressor(finalImage, {
          quality: 0.6,
          maxWidth: 800,
          maxHeight: 1000, // 4:5 aspect ratio (800 * 5/4)
          mimeType: 'image/jpeg',
          success(result) {
            resolve(result)
          },
          error(err) {
            reject(err)
          },
        })
      })
      const compressedFile = new File([compressedBlob], 'image.jpg', {
        type: 'image/jpeg',
      })
      onSave(compressedFile)
    } catch (error) {
      console.error('Compression failed, saving uncompressed:', error)
      onSave(finalImage)
    } finally {
      setIsCompressing(false)
    }
  }

  // Aspect ratio for backend: 2400px x 3000px = 4:5 (vertical)
  const CROP_ASPECT_RATIO = 4 / 5 // 0.8

  const stencilSize: StencilSize = ({ boundary }) => {
    // Calculate size to maintain 4:5 aspect ratio
    const maxWidth = boundary.width
    const maxHeight = boundary.height

    // Calculate dimensions maintaining aspect ratio
    let width = maxWidth
    let height = width / CROP_ASPECT_RATIO

    // If height exceeds boundary, scale down
    if (height > maxHeight) {
      height = maxHeight
      width = height * CROP_ASPECT_RATIO
    }

    return {
      width,
      height,
    }
  }

  const defaultSize: DefaultSize = ({ imageSize }) => {
    // Default to filling available space while maintaining aspect ratio
    const maxWidth = Math.min(imageSize.width, imageSize.height * CROP_ASPECT_RATIO)
    const maxHeight = Math.min(imageSize.height, imageSize.width / CROP_ASPECT_RATIO)

    return {
      width: maxWidth,
      height: maxHeight,
    }
  }

  const onReady = () => {
    setIsCropperReady(true)
  }

  // Calculate cropper size to fit 4:5 aspect ratio
  // Use a reasonable max size that fits on screen
  // Target size: ~400px width (which gives ~500px height at 4:5 ratio)
  // Calculates the maximum width for the cropper, choosing the smaller value between 400px and the current window width minus 90px (to ensure padding from screen edges).
  const maxCropperWidth = Math.min(400, globalThis.window ? globalThis.window.innerWidth : 400)
  const widthToUse = cropperWidth || maxCropperWidth
  const cropperSize = `${widthToUse}px`

  useEffect(() => {
    if (!isOpen) return

    const measure = () => {
      const containerWidth = containerRef.current?.offsetWidth ?? 0
      const nextWidth = Math.min(400, containerWidth)
      if (nextWidth > 0) {
        setCropperWidth(nextWidth)
      }
    }

    const timeoutId = globalThis.setTimeout(() => {
      measure()
    }, 150)
    const handleResize = () => {
      measure()
    }
    globalThis.window?.addEventListener('resize', handleResize)
    return () => {
      globalThis.clearTimeout(timeoutId)
      globalThis.window?.removeEventListener('resize', handleResize)
    }
  }, [isOpen, cropperSize, maxCropperWidth, cropperWidth])

  // Reset ready state when modal closes or image changes
  useEffect(() => {
    setIsCropperReady(false)
  }, [isOpen, image])

  return (
    <>
      <style>{`
        .cropper-overlay-hidden {
          display: none !important;
        }
        .cropper-loading {
          opacity: 0;
          transition: opacity 0.2s ease-in;
        }
        .cropper-ready {
          opacity: 1;
        }
      `}</style>
      <Modal isOpen={isOpen} onClose={onClose} isCentered>
        <ModalOverlay />
        <ModalContent w="calc(100vw - 32px)" maxW="420px" mx="4">
          <ModalCloseButton />
          <ModalBody px="6" pb="6">
            <Text variant="title" fontSize="2xl" fontWeight="500" mb="20px">
              Crop your post image
            </Text>

            <Box ref={containerRef} mx="auto" width="100%" display="flex" justifyContent="center">
              <FixedCropper
                className={isCropperReady ? 'cropper-ready' : 'cropper-loading'}
                style={{
                  width: cropperSize,
                  height: `calc(${cropperSize} * 5 / 4)`,
                  margin: '0 auto',
                }}
                minWidth={150}
                minHeight={Math.round(150 / CROP_ASPECT_RATIO)}
                defaultSize={defaultSize}
                stencilSize={stencilSize}
                stencilProps={{
                  aspectRatio: CROP_ASPECT_RATIO,
                  movable: false,
                  resizable: false,
                  handlers: {},
                  lines: {},
                  scalable: false,
                  overlayClassName: 'cropper-overlay-hidden',
                }}
                imageRestriction={ImageRestriction.stencil}
                transitions={false}
                onReady={onReady}
                ref={cropperRef}
                src={image}
              />
            </Box>
          </ModalBody>
          <ModalFooter>
            <Button
              variant="heavy"
              size="sm"
              isLoading={isLoading || isCompressing}
              onClick={() => onCrop()}
            >
              Save
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  )
}

export default ImageCropModal
